import React from 'react'
import { Popover } from 'react-bootstrap'
import classNames from 'classnames'
import { formatFriendlyDateTime } from '../../../common/dateUtils'
import { type CrossClientAction, CrossClientActionPhases } from '../../../models/CrossClient'
import { CRM_ACTION_REGISTRY, resolveCrmActionKind } from './crmActionRegistry'
import styles from './CrossClientActionLogPopover.module.scss'

const formatActionTimestamp = (action: CrossClientAction) => {
  const date = new Date(action.finishedAt ?? action.startedAt)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return formatFriendlyDateTime(date)
}

const getActionSortKey = (action: CrossClientAction) => action.finishedAt ?? action.startedAt

export interface CrossClientActionLogPopoverProps {
  actions: CrossClientAction[]
  clientId: string
  clientName: string
}

// A function rather than a component: OverlayTrigger needs the Popover element itself to position it.
export const crossClientActionLogPopover = ({ actions, clientId, clientName }: CrossClientActionLogPopoverProps): React.ReactElement => {
  const sortedActions = [...actions].sort((a, b) => getActionSortKey(b).localeCompare(getActionSortKey(a)))
  return (
    <Popover id={`cross-client-action-popover-${clientId}`} className={styles.popover}>
      <Popover.Body>
        <div className={classNames(styles.popoverHeader, 'paragraph-medium')}>
          Actions taken on <strong>{clientName}</strong>
        </div>
        {sortedActions.map((action) => {
          const kind = resolveCrmActionKind(action)
          if (!kind) {
            return null
          }
          const descriptor = CRM_ACTION_REGISTRY[kind]
          return (
            <div key={action.id} className={styles.popoverRow}>
              <i className={classNames('bi', descriptor.icon)} />
              <div className='d-flex flex-column'>
                <span className={classNames(styles.popoverRowTitle, 'paragraph-medium')}>
                  {action.phase === CrossClientActionPhases.COMPLETED ? descriptor.completedLabel : descriptor.inProgressLabel}
                </span>
                <span className={classNames(styles.popoverRowTime, 'paragraph-small')}>{formatActionTimestamp(action)}</span>
              </div>
            </div>
          )
        })}
      </Popover.Body>
    </Popover>
  )
}
